import React from 'react';
import styled from 'styled-components';
import { space, width, height, display } from 'styled-system';
import { Classes, Tabs as BPTabs } from '@blueprintjs/core';
import classNames from 'classnames';

import { Flex, FlexColumn } from 'core/components';
import { useTabable } from 'core/util/ariaUtils';
import flexHelper from './utils/flex';

const StyledTabs = styled(BPTabs)`
  ${space};
  ${width};
  ${height};
  ${display};
  ${flexHelper};

  /* some reasonable resets */
  .${Classes.TABS} {
    min-width: 0;
    background: transparent;
  }

  .${Classes.TAB_LIST} {
    margin-top: ${({ theme, listProps = {} }) => `${theme.space[listProps?.mt]}px`}};
  }

  .${Classes.TAB_PANEL}.legend-tab:not([aria-hidden='true']) {
    display: flex;
    overflow: auto;
    flex: 1;
  }

  .bp4-tab:not([aria-disabled='true']):not([aria-selected='true']):hover {
    background-color: ${({ theme }) => theme.backgrounds.tableHeader} !important;
    color: ${({ theme }) => theme.colors.text} !important;
  }

  &.${Classes.TABS}.bp4-vertical .${Classes.TAB_LIST} {
      // tabs are not aligned to the top of the container w/o this (vertical case)
      height: auto;
      min-height: 0;
  }

  &.flexed {
    flex: 1;
    display: flex;
    flex-direction: ${({ vertical }) => (vertical ? 'row' : 'column')};

    > .${Classes.TAB_PANEL} {
      flex: 1;
      // Adjust padding based on vertical prop - this is offset by the negative margin on the Flex component BUT outer borders are covered otherwise
      padding: ${({ vertical }) => (vertical ? '1px 1px 1px 16px' : '1px 1px 16px 1px')};
      box-sizing: border-box; // Ensure padding is included in width/height calculations
    }
  }

  &.overflowed {
    overflow: hidden;
    min-height: 0;

    > .${Classes.TAB_PANEL} {
      overflow: auto;
      min-height: 0;
    }
  }

  &.bordered > .${Classes.TAB_LIST} {
    border-bottom: ${({ theme }) => theme.borders.thin};
  }

  &.fullBorder > .${Classes.TAB_LIST} {
    background: ${({ background }) => background || 'transparent'};
    border: ${({ theme }) => theme.borders.thinLighter};
    border-radius: 5px;
  }
`;

const defaultOuterFlexStyles = {
  margin: '-1px', // Counteract the padding we'll add to the tab panel
  position: 'relative' // Ensure the negative margin doesn't affect siblings
};

const Tabs = (props) => {
  const {
    fullBorder,
    minWidth,
    borderRadius,
    flexed,
    overflowed,
    bordered,
    className,
    vertical,
    outerFlexStyles = defaultOuterFlexStyles,
    ...rest
  } = props;

  // I tried many different ways but Blueprint does not tend to let you set a plain ref, just sometimes gives you special access
  // to the DOM node like "inputRef" - so we agreed to use a wrapper div.
  const tabSimpleRef = useTabable();

  const FlexComponent = vertical ? Flex : FlexColumn;

  return (
    // It would appear that choosing one flex direction does not fix all so pls give us a bit to fix.  For now, Tabs are not fully VPAT compliant.
    <FlexComponent ref={tabSimpleRef} flex={1} style={outerFlexStyles}>
      <StyledTabs
        {...rest}
        vertical={vertical}
        style={{
          minWidth,
          borderRadius,
          display: 'flex', // Ensure it's using flex layout
          flexDirection: vertical ? 'column' : 'row' // Set the direction based on the vertical prop:
        }}
        className={classNames(className, {
          overflowed,
          bordered,
          flexed: true, // there are currently only 6 instances of us using flexed and now ALL will have it but testing seems to indicate that this is fine.
          fullBorder
        })}
      />
    </FlexComponent>
  );
};

export default Tabs;
